import { useEffect, useState } from 'react';
import { AdminLayout } from '@/components/admin/AdminLayout';
import { StatCard } from '@/components/admin/StatCard';
import { useAdminApi } from '@/hooks/useAdminApi';
import { HardDrive, Users, AlertTriangle, TrendingUp } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Skeleton } from '@/components/ui/skeleton';

interface User {
  id: string;
  email: string;
  storage_limit_mb: number;
  storage_used_mb: number;
}

export default function AdminStorage() {
  const { getUsers, isLoading } = useAdminApi();
  const [users, setUsers] = useState<User[]>([]);

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const data = await getUsers();
        setUsers(data);
      } catch (error) {
        console.error('Failed to fetch users:', error);
      }
    };
    fetchUsers();
  }, [getUsers]);

  const totalStorageUsed = users.reduce((sum, u) => sum + u.storage_used_mb, 0);
  const totalStorageLimit = users.reduce((sum, u) => sum + u.storage_limit_mb, 0);
  const avgStoragePerUser = users.length > 0 ? totalStorageUsed / users.length : 0;
  const usersNearLimit = users.filter(u => (u.storage_used_mb / u.storage_limit_mb) > 0.8).length;

  const formatStorage = (mb: number) => {
    if (mb >= 1024) {
      return `${(mb / 1024).toFixed(2)} GB`;
    }
    return `${mb.toFixed(1)} MB`;
  };

  const sortedByUsage = [...users].sort((a, b) => b.storage_used_mb - a.storage_used_mb);

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold">Storage Analytics</h1>
          <p className="text-muted-foreground">Monitor storage usage across all users</p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {isLoading ? (
            <>
              {[1, 2, 3, 4].map((i) => (
                <Card key={i}>
                  <CardContent className="p-6">
                    <Skeleton className="h-4 w-20 mb-2" />
                    <Skeleton className="h-8 w-16" />
                  </CardContent>
                </Card>
              ))}
            </>
          ) : (
            <>
              <StatCard
                title="Total Storage Used"
                value={formatStorage(totalStorageUsed)}
                icon={HardDrive}
              />
              <StatCard
                title="Average Per User"
                value={formatStorage(avgStoragePerUser)}
                icon={TrendingUp}
              />
              <StatCard
                title="Users Near Limit (>80%)"
                value={usersNearLimit}
                icon={AlertTriangle}
              />
              <StatCard
                title="Total Allocated"
                value={formatStorage(totalStorageLimit)}
                icon={Users}
              />
            </>
          )}
        </div>

        {/* Storage by User */}
        <Card>
          <CardHeader>
            <CardTitle>Storage by User</CardTitle>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="space-y-4">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="space-y-2">
                    <Skeleton className="h-4 w-48" />
                    <Skeleton className="h-4 w-full" />
                  </div>
                ))}
              </div>
            ) : users.length === 0 ? (
              <p className="text-muted-foreground text-center py-8">No users yet</p>
            ) : (
              <div className="space-y-4">
                {sortedByUsage.map((user) => {
                  const percent = (user.storage_used_mb / user.storage_limit_mb) * 100;
                  return (
                    <div key={user.id} className="space-y-2">
                      <div className="flex items-center justify-between text-sm">
                        <span className="font-medium truncate max-w-[200px]">{user.email}</span>
                        <span className="text-muted-foreground">
                          {user.storage_used_mb.toFixed(1)} / {user.storage_limit_mb} MB ({percent.toFixed(0)}%)
                        </span>
                      </div>
                      <Progress 
                        value={Math.min(percent, 100)} 
                        className={`h-2 ${percent > 80 ? '[&>div]:bg-destructive' : percent > 50 ? '[&>div]:bg-yellow-500' : ''}`}
                      />
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Users Near Limit */}
        {usersNearLimit > 0 && (
          <Card className="border-destructive/50">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-destructive">
                <AlertTriangle className="w-5 h-5" />
                Users Near Storage Limit
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {users
                  .filter(u => (u.storage_used_mb / u.storage_limit_mb) > 0.8)
                  .map((user) => {
                    const percent = (user.storage_used_mb / user.storage_limit_mb) * 100;
                    return (
                      <div key={user.id} className="flex items-center justify-between p-3 bg-destructive/10 rounded-lg">
                        <span className="font-medium">{user.email}</span>
                        <span className="text-destructive font-semibold">{percent.toFixed(0)}% used</span>
                      </div>
                    );
                  })}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </AdminLayout>
  );
}
